import * as THREE from 'three'

import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'
import { WEBGL } from 'three/examples/jsm/WebGL.js'

interface Dimensions {
  height: number
  width: number
}

interface RenderEntry {
  dimensions: Dimensions
  url: string
  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  model: THREE.Object3D
  callback: (img: string) => void
}

interface RenderResults {
  data: string
  dimensions: Dimensions
}

class ThreeDRenderer {
  hasWebGL = false
  isRendering = false
  renderer = undefined as unknown as THREE.WebGLRenderer
  renderQueue = [] as RenderEntry[]

  renderedUrls = {} as { [key: string]: RenderResults }

  constructor() {
    if (!WEBGL.isWebGLAvailable()) return
    this.hasWebGL = true
    this.renderer = new THREE.WebGLRenderer({ preserveDrawingBuffer: true })
  }

  async renderModel(src: string, height: number, width: number, callback: (img: string) => void) {
    const cachedImg = this.renderedUrls[src]
    if (cachedImg && !this.isBiggerThan({ height, width }, cachedImg.dimensions)) {
      callback(cachedImg.data)
      return
    }

    const loadData = await load3DFile(src)
    if (loadData.error || !loadData.model) return loadData.error

    this.renderer.setSize(width, height)
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(0xffffff)
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000)
    camera.position.z = 100

    addModelToScene(loadData.model, scene)
    addLights(scene)

    this.render(src, scene, camera, loadData.model, callback, { height, width })
  }

  render(
    url: string,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    model: THREE.Object3D,
    callback: (img: string) => void,
    dimensions: Dimensions
  ): void {
    this.renderQueue.push({ dimensions, url, scene, camera, model, callback })
    this.processQueue()
  }

  processQueue(): void {
    if (this.isRendering) return
    this.isRendering = true

    while (this.renderQueue.length > 0) {
      const entry = this.renderQueue.shift()
      if (!entry) continue

      const cachedImg = this.renderedUrls[entry.url]
      if (cachedImg && !this.isBiggerThan(entry.dimensions, cachedImg.dimensions)) {
        entry.callback(cachedImg.data)
        continue
      }

      this.renderer.render(entry.scene, entry.camera)
      const imgData = this.renderer.domElement.toDataURL('image/png')
      entry.callback(imgData)
      this.renderedUrls[entry.url] = { data: imgData, dimensions: entry.dimensions }
    }

    this.isRendering = false
  }

  isBiggerThan(newD: Dimensions, oldD: Dimensions): boolean {
    if (newD.height > oldD.height || newD.width > oldD.width) return true
    return false
  }
}

export default new ThreeDRenderer()

interface LoadResponse {
  model?: THREE.Object3D
  error?: string
}

export const load3DFile = async (src: string): Promise<LoadResponse> => {
  // Determine the loader we should use based on the file extension.
  let stlLoader: STLLoader
  let objLoader: OBJLoader
  let fbxLoader: FBXLoader
  const fileUrl = src ? src : ''
  const file_ext: RegExpMatchArray | null = fileUrl.match(/\.(stl|obj|fbx)$/gi)
  const file_ext_str: string = file_ext ? file_ext[0] : ''
  switch (file_ext_str) {
    case '.stl':
    case '.STL':
      // Setup the STL loader.
      stlLoader = new STLLoader()
      return await new Promise<LoadResponse>(resolve => {
        stlLoader.load(fileUrl, geometry => {
          geometry.center()
          const material = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide })
          const model = new THREE.Mesh(geometry, material)
          resolve({ model })
        })
      })

    case '.obj':
    case '.OBJ':
      objLoader = new OBJLoader()
      return await new Promise<LoadResponse>(resolve => {
        objLoader.load(fileUrl, (object: THREE.Group) => {
          const material = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide })

          // Now we need to traverse the object's properties looking for the mesh so we can apply the material.
          object.traverse(child => {
            if (child instanceof THREE.Mesh) {
              child.material = material
            }
          })

          // Since this object is already mesh, just assign it to our classed model.
          resolve({ model: object })
        })
      })

    case '.fbx':
    case '.FBX':
      fbxLoader = new FBXLoader()
      return await new Promise<LoadResponse>(resolve => {
        fbxLoader.load(fileUrl, object => {
          const material = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide })

          // Now we need to traverse the object's properties looking for the mesh so we can apply the material.
          object.traverse(child => {
            if (child instanceof THREE.Mesh) {
              child.material = material
            }
          })

          // Since this object is already mesh, just assign it to our classed model.
          resolve({ model: object })
        })
      })

    default:
      return { error: 'Invalid 3D file format' }
  }
}

export const addModelToScene = (model: THREE.Object3D, scene: THREE.Scene): void => {
  model.castShadow = true
  model.receiveShadow = true

  // Scale the model so that it fits within the viewer
  let box = new THREE.Box3().setFromObject(model)
  let longSide = 'x'
  let longestMeasure = box.max.x - box.min.x

  let cmpMeasure = box.max.y - box.min.y
  if (cmpMeasure > longestMeasure) {
    longSide = 'y'
    longestMeasure = cmpMeasure
  }

  cmpMeasure = box.max.z - box.min.z
  if (cmpMeasure > longestMeasure) {
    longSide = 'z'
    longestMeasure = cmpMeasure
  }

  const scaleFactor = longSide === 'x' ? 120 : longSide === 'y' ? 100 : 90

  const scale = scaleFactor / longestMeasure
  model.scale.set(scale, scale, scale)

  // Center the model in the scene
  box = new THREE.Box3().setFromObject(model)
  const midX = (box.min.x - box.max.x) / 2
  const midY = (box.min.y - box.max.y) / 2
  const midZ = (box.min.z - box.max.z) / 2
  model.translateX(midX - box.min.x)
  model.translateY(midY - box.min.y)
  model.translateZ(midZ - box.min.z)

  // Angle to model to give a clearer inital view
  model.rotation.set(0.3, -0.3, 0)

  // We only want to show one model at a time so we wipe the scene first
  // in case the 'src' prop has updated since initialization
  scene.clear()
  scene.add(model)
}

export const addLights = (scene: THREE.Scene): void => {
  // Create lights for this scene so the lambert material has something to use.
  const light = new THREE.DirectionalLight(0xffffff, 1)
  light.position.set(50, 50, 50)
  scene.add(light)

  const light2 = new THREE.DirectionalLight(0xffffff, 1)
  light2.position.set(-50, -50, -50)
  scene.add(light2)
}
